import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { Clock, MapPin, Calendar, Sparkles, ChevronRight } from 'lucide-react';

export const ScheduleView: React.FC = () => {
  const { schedule, settings, setActiveTab } = useApp();
  const [selectedDay, setSelectedDay] = useState<string>('all');

  // Extract unique dates preserving schedule order
  const uniqueDates = useMemo(() => {
    const dates: string[] = [];
    schedule.forEach((item) => {
      const d = item.date?.trim() || 'General Schedule';
      if (!dates.includes(d)) {
        dates.push(d);
      }
    });
    return dates;
  }, [schedule]);

  // Group items by date
  const groupedSchedule = useMemo(() => {
    return uniqueDates.map((date) => ({
      date,
      items: schedule.filter((s) => (s.date?.trim() || 'General Schedule') === date),
    }));
  }, [schedule, uniqueDates]);

  // Filtered groups based on tab selection
  const displayedGroups = useMemo(() => {
    if (selectedDay === 'all') return groupedSchedule;
    return groupedSchedule.filter((g) => g.date === selectedDay);
  }, [groupedSchedule, selectedDay]);

  const totalSessionsCount = schedule.length;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
      {/* Header Banner */}
      <div className="mb-8 sm:mb-10 text-center sm:text-left border-b border-brand-tan pb-6">
        <div className="flex items-center justify-center sm:justify-start gap-3 mb-3 flex-wrap">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-tan/30 text-brand-black text-xs font-semibold uppercase tracking-wider border border-brand-warmgray/30">
            <Calendar className="w-3.5 h-3.5 text-brand-red" />
            <span>{settings.eventDate || 'October 15–16, 2026'}</span>
            {uniqueDates.length > 1 && (
              <span className="text-[10px] bg-brand-red text-white px-1.5 py-0.2 rounded font-bold ml-0.5">
                {uniqueDates.length}-Day Event
              </span>
            )}
          </div>
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-brand-black tracking-tight">
          Event Schedule
        </h1>
        <p className="mt-2 text-sm sm:text-base text-brand-subtext flex items-center justify-center sm:justify-start gap-2">
          <MapPin className="w-4 h-4 text-brand-red shrink-0" />
          <span>{settings.venue}</span>
        </p>
      </div>

      {/* Multi-Day Selector Tabs (if more than 1 date exists) */}
      {uniqueDates.length > 1 && (
        <div className="mb-8 flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none justify-start sm:justify-start">
          <button
            type="button"
            onClick={() => setSelectedDay('all')}
            className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap shadow-sm ${
              selectedDay === 'all'
                ? 'bg-brand-black text-white'
                : 'bg-white text-brand-subtext hover:text-brand-black border border-brand-tan hover:bg-brand-offwhite'
            }`}
          >
            All Sessions ({totalSessionsCount})
          </button>
          {uniqueDates.map((date, idx) => {
            const count = schedule.filter((s) => (s.date?.trim() || 'General Schedule') === date).length;
            const isSelected = selectedDay === date;
            return (
              <button
                key={date}
                type="button"
                onClick={() => setSelectedDay(date)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap flex items-center gap-1.5 shadow-sm ${
                  isSelected
                    ? 'bg-brand-red text-white'
                    : 'bg-white text-brand-subtext hover:text-brand-black border border-brand-tan hover:bg-brand-offwhite'
                }`}
              >
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                <span>Day {idx + 1}: {date.replace(/^Day\s*\d+\s*[-–—:]\s*/i, '')}</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${
                  isSelected ? 'bg-white/20 text-white' : 'bg-brand-tan/40 text-brand-black'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Grouped Day Sections */}
      <div className="space-y-12">
        {displayedGroups.map((group, groupIdx) => {
          // Find overall day index
          const dayNumber = uniqueDates.indexOf(group.date) + 1;

          return (
            <div key={group.date} className="space-y-6">
              {/* Day Section Header */}
              <div className="flex items-center gap-3 bg-[#F7F6F4] p-3 sm:p-4 rounded-2xl border border-brand-tan/80">
                <div className="px-3 h-8 rounded-xl bg-brand-red text-white flex items-center justify-center font-extrabold text-xs shrink-0 shadow-sm whitespace-nowrap">
                  Day {dayNumber > 0 ? dayNumber : groupIdx + 1}
                </div>
                <div className="min-w-0">
                  <h2 className="text-sm sm:text-base font-extrabold text-brand-black flex items-center gap-2">
                    <span>{group.date}</span>
                  </h2>
                  <p className="text-[11px] text-brand-subtext font-medium">
                    {group.items.length} {group.items.length === 1 ? 'session' : 'sessions'} scheduled
                  </p>
                </div>
              </div>

              {/* Timeline for this day */}
              <div className="relative pl-6 sm:pl-8 border-l-2 border-brand-tan space-y-6 sm:space-y-7 ml-3 sm:ml-4">
                {group.items.map((item) => {
                  const isMinistryPresentation =
                    item.title.toLowerCase().includes('ministry presentation') ||
                    (item.category === 'pitch' && !item.title.toLowerCase().includes('breakout'));
                  const isVotingSession = item.category === 'voting';
                  const isAwardsSession = item.category === 'awards';

                  return (
                    <div key={item.id} className="relative group">
                      {/* Timeline Marker Dot */}
                      <div
                        className={`absolute -left-[31px] sm:-left-[39px] top-1.5 w-4 h-4 rounded-full border-2 transition-all ${
                          isVotingSession
                            ? 'bg-brand-red border-white'
                            : isAwardsSession
                            ? 'bg-amber-500 border-white'
                            : 'bg-white border-brand-warmgray'
                        }`}
                      />

                      {/* Card */}
                      <div className="p-5 rounded-2xl border border-brand-tan hover:border-brand-warmgray bg-white shadow-sm transition-all">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 text-xs font-bold text-brand-red bg-brand-red/10 px-2.5 py-1 rounded-md border border-brand-red/20">
                              <Clock className="w-3 h-3" />
                              {item.time}
                            </span>
                            {item.date && (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-brand-black bg-brand-tan/30 px-2 py-0.5 rounded-md border border-brand-warmgray/20">
                                <Calendar className="w-3 h-3 text-brand-red/80" />
                                {item.date}
                              </span>
                            )}
                          </div>
                        </div>

                        <h3 className="text-lg font-bold text-brand-black group-hover:text-brand-red transition-colors flex items-center gap-2">
                          {item.title}
                          {isAwardsSession && <Sparkles className="w-4 h-4 text-amber-500" />}
                        </h3>

                        {item.description.includes('•') ? (
                          <div className="mt-3 space-y-2">
                            <p className="text-sm text-brand-subtext leading-relaxed">
                              {item.description.split('\n')[0]}
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 border-t border-brand-tan/50">
                              {item.description
                                .split('\n')
                                .slice(1)
                                .filter(Boolean)
                                .map((line, lIdx) => {
                                  const content = line.trim().replace(/^•\s*/, '');
                                  const parts = content.split('|');
                                  const ministryAndPresenter = parts[0]?.trim() || '';
                                  const room = parts[1]?.trim() || '';
                                  return (
                                    <div
                                      key={lIdx}
                                      className="flex items-center justify-between p-2.5 rounded-xl bg-[#F7F6F4] border border-brand-tan/70 text-xs shadow-xs"
                                    >
                                      <span className="font-bold text-brand-black truncate mr-2">
                                        {ministryAndPresenter}
                                      </span>
                                      {room && (
                                        <span className="shrink-0 px-2 py-0.5 rounded-md bg-brand-tan/60 text-brand-black font-semibold text-[10px] border border-brand-warmgray/40">
                                          {room}
                                        </span>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ) : (
                          <p className="mt-1.5 text-sm text-brand-subtext leading-relaxed whitespace-pre-line">
                            {item.description}
                          </p>
                        )}

                        {/* Contextual Action Button */}
                        {isMinistryPresentation && (
                          <div className="mt-4 pt-3 border-t border-brand-tan/60 flex items-center justify-end">
                            <button
                              type="button"
                              onClick={() => setActiveTab('projects')}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-black hover:text-brand-red transition-colors"
                            >
                              View Pitches & Proposals
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}

                        {isVotingSession && (
                          <div className="mt-4 pt-3 border-t border-brand-tan/60 flex items-center justify-between">
                            <span className="text-xs text-brand-subtext">All audience members evaluate ministries</span>
                            <button
                              onClick={() => setActiveTab('vote')}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-brand-red hover:bg-red-600 px-3 py-1.5 rounded-lg shadow-sm transition-all"
                            >
                              Open Ballot
                              <ChevronRight className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

